/**
 * Supplier catalog picker (BLU-1268): the pure half of `CatalogPicker`.
 *
 * A catalog style is a list of variants (colour name x size label). The picker
 * shows them as colour chips, size chips and a colour x size grid; every cell
 * of the grid carries a Supply (Stocked or Just in time). Adding turns the
 * picked cells into the org's own blanks: the product code, plus the org's
 * own size and colour codes for each catalog name (`CODE-SIZE-COLOUR`).
 *
 * Code mapping follows the rule for code-keyed data (CLAUDE.md, "show the
 * org's OWN names"): a catalog name the org's code table already knows maps
 * to that code; anything else gets a suggested new code the operator can
 * change, and typing one of the org's existing codes maps to it. Nothing here
 * ever blocks on a missing table: an empty table only means every code is new.
 *
 * Pure: no React; words come in through the caller.
 */
import {
  BASE_GARMENT_IMAGE_URL_RE,
  BASE_GARMENT_NAME_MAX,
  BASE_GARMENT_SEGMENT_NAME_MAX,
  CATALOG_MAX_CELLS,
  NEW_PRODUCT_CODE_RE,
  NEW_SEGMENT_CODE_RE,
  composeBlankSku,
  type AddStockMode,
  type BaseGarmentAudience,
  type CatalogAddToLocationInput,
  type CostCurrency,
} from '@threadheads/shared';
import type { RouterOutputs } from '@/lib/trpc';

export type CatalogSupplier = RouterOutputs['supplierCatalog']['listSuppliers'][number];
/** A row of the style list: no variants, their counts only. */
export type CatalogStyleSummary = RouterOutputs['supplierCatalog']['getStyles'][number];
/** One opened style with its variants (`getStyle`). */
export type CatalogStyle = RouterOutputs['supplierCatalog']['getStyle'];
export type CatalogVariant = CatalogStyle['variants'][number];
export type CatalogAddResult = RouterOutputs['supplierCatalog']['addToLocation'];
export type OrgCodeTables = RouterOutputs['baseGarments']['codeTables'];

/** The Supply a new blank gets when nothing else is picked (Brett, concept D). */
export const DEFAULT_ADD_MODE: AddStockMode = 'just_in_time';

/** NUL separates key parts: a colour name or size label never contains it (DESIGN.md section 12.1). */
const KEY_SEP = '\u0000';
/**
 * The size key of a style with no sizes (a cap, a tote): its one "One size"
 * chip and grid column. Its blanks have no size part (`CODE-COLOUR`).
 */
export const NO_SIZE_KEY = '';

const normalise = (value: string): string => value.trim().toLowerCase();

export const cellKey = (colourKey: string, sizeKey: string): string => `${colourKey}${KEY_SEP}${sizeKey}`;

// ── Axes ──────────────────────────────────────────────────────────────────────

export interface CatalogColour {
  /** Lower-cased name: two spellings of one colour are one chip. */
  key: string;
  name: string;
  /** The supplier's swatch, when the catalog has one. */
  hex: string | null;
  /** The colour's photo: the first https image among its variants (`catalogImage`), or null. */
  image: string | null;
}

export interface CatalogSize {
  key: string;
  label: string;
  sort: number;
}

export interface StyleAxes {
  colours: CatalogColour[];
  /** The supplier's sizes; empty for a one-size style. */
  sizes: CatalogSize[];
  /** Every variant has no size: one "One size" chip and column (`NO_SIZE_KEY`). */
  sizeless: boolean;
  /** The catalog variant behind each colour x size cell. */
  variantAt: ReadonlyMap<string, CatalogVariant>;
}

const HEX_RE = /^#?[0-9a-fA-F]{6}$/;

/** A swatch the browser can paint, or null. */
export function swatchHex(hex: string | null | undefined): string | null {
  if (!hex || !HEX_RE.test(hex.trim())) return null;
  const value = hex.trim();
  return value.startsWith('#') ? value : `#${value}`;
}

/**
 * A catalog photo the browser may load, or null: https only, no whitespace
 * (the base garment image rule). Never rewritten: the supplier's own address.
 */
export function catalogImage(url: string | null | undefined): string | null {
  const value = url?.trim();
  return value && BASE_GARMENT_IMAGE_URL_RE.test(value) ? value : null;
}

/**
 * The colours (in the catalog's order, by name), the sizes (by the supplier's
 * size order) and the variant of each cell. A style that mixes sized and
 * size-less variants keeps its sized ones (a blank is one shape or the other).
 * The style list counts colours by the same rule on the server
 * (`catalogStyleCounts`, shared), so its "5 colours" is the chips shown here.
 */
export function styleAxes(style: Pick<CatalogStyle, 'variants'>): StyleAxes {
  const sizeless = style.variants.length > 0 && style.variants.every((variant) => !variant.sizeLabel?.trim());
  const colours = new Map<string, CatalogColour>();
  const sizes = new Map<string, CatalogSize>();
  const variantAt = new Map<string, CatalogVariant>();
  for (const variant of style.variants) {
    const colourName = variant.colourName.trim();
    if (colourName === '') continue;
    const label = variant.sizeLabel?.trim() ?? '';
    if (!sizeless && label === '') continue;
    const colourKey = normalise(colourName);
    const sizeKey = sizeless ? NO_SIZE_KEY : normalise(label);
    let colour = colours.get(colourKey);
    if (!colour) {
      colour = { key: colourKey, name: colourName, hex: swatchHex(variant.colourHex), image: null };
      colours.set(colourKey, colour);
    }
    // The colour's photo: the first https image among its variants.
    if (colour.image === null) colour.image = catalogImage(variant.imageUrl);
    if (!sizeless && !sizes.has(sizeKey)) sizes.set(sizeKey, { key: sizeKey, label, sort: variant.sizeSort });
    const key = cellKey(colourKey, sizeKey);
    if (!variantAt.has(key)) variantAt.set(key, variant);
  }
  const sizeList = [...sizes.values()].sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
  return { colours: [...colours.values()], sizes: sizeList, sizeless, variantAt };
}

/**
 * The sizes picked when a style opens: every size, or the one "One size" chip
 * of a style with no sizes (so a cap needs only a colour).
 */
export function initialPickedSizes(axes: Pick<StyleAxes, 'sizes' | 'sizeless'>): Set<string> {
  return new Set(axes.sizeless ? [NO_SIZE_KEY] : axes.sizes.map((size) => size.key));
}

// ── Guide price ───────────────────────────────────────────────────────────────

export interface GuidePrice {
  min: number;
  max: number;
  currency: string | null;
}

/** The supplier's list price across the style's variants: one figure, or a range. */
export function guidePrice(variants: readonly CatalogVariant[]): GuidePrice | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let currency: string | null = null;
  for (const variant of variants) {
    if (typeof variant.listPrice !== 'number' || !Number.isFinite(variant.listPrice)) continue;
    min = Math.min(min, variant.listPrice);
    max = Math.max(max, variant.listPrice);
    currency ??= variant.listPriceCurrency ?? null;
  }
  return Number.isFinite(min) ? { min, max, currency } : null;
}

// ── Supply per cell ──────────────────────────────────────────────────────────

/** Only the cells that differ from the style's Supply are kept. */
export type SupplyOverrides = ReadonlyMap<string, AddStockMode>;

export const otherMode = (mode: AddStockMode): AddStockMode => (mode === 'just_in_time' ? 'stocked' : 'just_in_time');

export const effectiveMode = (overrides: SupplyOverrides, key: string, defaultMode: AddStockMode): AddStockMode =>
  overrides.get(key) ?? defaultMode;

/** One cell switches to the other Supply; back to the style's own drops the override. */
export function toggleOverride(overrides: SupplyOverrides, key: string, defaultMode: AddStockMode): Map<string, AddStockMode> {
  const next = new Map(overrides);
  const flipped = otherMode(effectiveMode(overrides, key, defaultMode));
  if (flipped === defaultMode) next.delete(key);
  else next.set(key, flipped);
  return next;
}

/** A new style Supply keeps every cell that still differs from it. */
export function rebaseOverrides(overrides: SupplyOverrides, defaultMode: AddStockMode): Map<string, AddStockMode> {
  return new Map([...overrides].filter(([, mode]) => mode !== defaultMode));
}

export interface PickedCell {
  key: string;
  colour: CatalogColour;
  /** null for a style with no sizes. */
  size: CatalogSize | null;
  variant: CatalogVariant;
  mode: AddStockMode;
}

/**
 * The org already has this cell's blank at the location the styles were read
 * for (`getStyles` with a `locationId`). Such a cell is shown, never added again.
 */
export const isAddedCell = (axes: StyleAxes, key: string): boolean => axes.variantAt.get(key)?.addedAtLocation === true;

/**
 * Every catalog cell the picked colours and sizes make, colour by colour, in
 * size order. A one-size style's cells count only while its One size chip
 * (`NO_SIZE_KEY`) is picked.
 */
function eachPickedVariant(
  axes: StyleAxes,
  pickedColours: ReadonlySet<string>,
  pickedSizes: ReadonlySet<string>,
  visit: (key: string, colour: CatalogColour, size: CatalogSize | null, variant: CatalogVariant) => void,
): void {
  const sizeList: Array<CatalogSize | null> = axes.sizeless
    ? pickedSizes.has(NO_SIZE_KEY)
      ? [null]
      : []
    : axes.sizes.filter((size) => pickedSizes.has(size.key));
  for (const colour of axes.colours) {
    if (!pickedColours.has(colour.key)) continue;
    for (const size of sizeList) {
      const key = cellKey(colour.key, size?.key ?? NO_SIZE_KEY);
      const variant = axes.variantAt.get(key);
      if (variant) visit(key, colour, size, variant);
    }
  }
}

/**
 * The cells an Add would write: the picked colours and sizes, colour by
 * colour, in size order, leaving out the ones already at the location.
 */
export function pickedCells(
  axes: StyleAxes,
  pickedColours: ReadonlySet<string>,
  pickedSizes: ReadonlySet<string>,
  overrides: SupplyOverrides,
  defaultMode: AddStockMode,
): PickedCell[] {
  const cells: PickedCell[] = [];
  eachPickedVariant(axes, pickedColours, pickedSizes, (key, colour, size, variant) => {
    if (variant.addedAtLocation === true) return;
    cells.push({ key, colour, size, variant, mode: effectiveMode(overrides, key, defaultMode) });
  });
  return cells;
}

/** How many picked cells are already at the location (shown with a tick, not added). */
export function addedCellCount(
  axes: StyleAxes,
  pickedColours: ReadonlySet<string>,
  pickedSizes: ReadonlySet<string>,
): number {
  let count = 0;
  eachPickedVariant(axes, pickedColours, pickedSizes, (_key, _colour, _size, variant) => {
    if (variant.addedAtLocation === true) count += 1;
  });
  return count;
}

export interface SupplyCounts {
  total: number;
  justInTime: number;
  stocked: number;
}

export function supplyCounts(cells: readonly PickedCell[]): SupplyCounts {
  const justInTime = cells.filter((cell) => cell.mode === 'just_in_time').length;
  return { total: cells.length, justInTime, stocked: cells.length - justInTime };
}

// ── The org's own codes ─────────────────────────────────────────────────────

export type AxisKind = 'colours' | 'sizes';

export interface OrgCodeIndex {
  /** Lower-cased store name to its code. */
  byName: ReadonlyMap<string, string>;
  /** Code to the first store name listed for it. */
  byCode: ReadonlyMap<string, string>;
}

const EMPTY_INDEX: OrgCodeIndex = { byName: new Map(), byCode: new Map() };

export function indexCodeTable(entries: ReadonlyArray<{ name: string; code: string }> | undefined): OrgCodeIndex {
  if (!entries) return EMPTY_INDEX;
  const byName = new Map<string, string>();
  const byCode = new Map<string, string>();
  for (const entry of entries) {
    const code = entry.code.trim().toUpperCase();
    const name = entry.name.trim();
    if (code === '' || name === '') continue;
    if (!byName.has(normalise(name))) byName.set(normalise(name), code);
    if (!byCode.has(code)) byCode.set(code, name);
  }
  return { byName, byCode };
}

const alnumUpper = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * A new code for a catalog name, unique against `taken`: the first three
 * letters of a one-word name (Black is BLA), the initials of a longer one
 * (Heather Grey is HG), at most 4 characters; a digit is added on a clash.
 */
export function suggestSegmentCode(name: string, taken: ReadonlySet<string>): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map(alnumUpper)
    .filter((word) => word !== '');
  let base = words.length > 1 ? words.map((word) => word[0]).join('') : (words[0] ?? '').slice(0, 3);
  base = base.slice(0, 4) || 'X';
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base.slice(0, 4 - String(n).length)}${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return base;
}

/**
 * The org's existing code for a catalog name: its store name, or (sizes only)
 * a label that already is one of its codes ("M" is the size code M).
 */
export function existingCodeFor(kind: AxisKind, name: string, index: OrgCodeIndex): string | null {
  const byName = index.byName.get(normalise(name));
  if (byName) return byName;
  if (kind === 'sizes') {
    const asCode = alnumUpper(name);
    if (asCode !== '' && index.byCode.has(asCode)) return asCode;
  }
  return null;
}

export interface AxisValue {
  key: string;
  name: string;
}

/** The code each value starts with: the org's own, else a new one no other value takes. */
export function defaultCodes(kind: AxisKind, values: readonly AxisValue[], index: OrgCodeIndex): Map<string, string> {
  const out = new Map<string, string>();
  const taken = new Set<string>(index.byCode.keys());
  for (const value of values) {
    const existing = existingCodeFor(kind, value.name, index);
    if (existing) out.set(value.key, existing);
  }
  for (const value of values) {
    if (out.has(value.key)) continue;
    const code = suggestSegmentCode(value.name, taken);
    taken.add(code);
    out.set(value.key, code);
  }
  return out;
}

export type CodeProblem =
  | { kind: 'empty' }
  | { kind: 'rule' }
  /** The catalog name is already the org's, under another code. */
  | { kind: 'nameHasCode'; code: string }
  /** The colourless code is not a colour. */
  | { kind: 'colourless' }
  /** Another picked value resolves to the same code. */
  | { kind: 'twice'; other: string };

export interface ResolvedCode {
  key: string;
  name: string;
  code: string;
  /** The org's store name when the code is already its own. */
  existingName: string | null;
  problem: CodeProblem | null;
}

export interface ResolvedAxis {
  rows: ResolvedCode[];
  input: { none: false; codes: string[]; newCodes: Array<{ name: string; code: string }> };
  /** Code per value key, valid rows only. */
  codeOf: ReadonlyMap<string, string>;
  problems: number;
  newCount: number;
  existingCount: number;
}

/**
 * Reads one axis: each picked value's code (typed, else its default), whether
 * it is the org's own or new, and why a row can't be sent.
 */
export function resolveAxis(
  kind: AxisKind,
  values: readonly AxisValue[],
  typed: Readonly<Record<string, string>>,
  index: OrgCodeIndex,
  colourlessCode: string | null,
): ResolvedAxis {
  const defaults = defaultCodes(kind, values, index);
  const rows: ResolvedCode[] = values.map((value) => {
    const code = (typed[value.key] ?? defaults.get(value.key) ?? '').trim().toUpperCase();
    const existingName = index.byCode.get(code) ?? null;
    let problem: CodeProblem | null = null;
    if (code === '') problem = { kind: 'empty' };
    else if (kind === 'colours' && colourlessCode && code === colourlessCode.trim().toUpperCase()) problem = { kind: 'colourless' };
    else if (existingName === null) {
      if (!NEW_SEGMENT_CODE_RE.test(code)) problem = { kind: 'rule' };
      else {
        const nameCode = index.byName.get(normalise(value.name));
        if (nameCode && nameCode !== code) problem = { kind: 'nameHasCode', code: nameCode };
      }
    }
    return { key: value.key, name: value.name, code, existingName, problem };
  });

  // Two values on one code would make one SKU twice.
  const byCode = new Map<string, ResolvedCode[]>();
  for (const row of rows) {
    if (row.code === '') continue;
    byCode.set(row.code, [...(byCode.get(row.code) ?? []), row]);
  }
  for (const group of byCode.values()) {
    if (group.length < 2) continue;
    for (const row of group) {
      if (row.problem) continue;
      const other = group.find((candidate) => candidate !== row);
      if (other) row.problem = { kind: 'twice', other: other.name };
    }
  }

  const codes: string[] = [];
  const newCodes: Array<{ name: string; code: string }> = [];
  const codeOf = new Map<string, string>();
  for (const row of rows) {
    if (row.problem) continue;
    codeOf.set(row.key, row.code);
    if (row.existingName !== null) codes.push(row.code);
    else newCodes.push({ name: row.name.slice(0, BASE_GARMENT_SEGMENT_NAME_MAX), code: row.code });
  }
  return {
    rows,
    input: { none: false, codes, newCodes },
    codeOf,
    problems: rows.filter((row) => row.problem !== null).length,
    newCount: newCodes.length,
    existingCount: codes.length,
  };
}

// ── The product code ─────────────────────────────────────────────────────────

/**
 * The product code a style starts with: the catalog's suggestion, else the
 * style code, led by the supplier's first letters when it starts with a digit
 * (AS Colour 5001 is ASC5001). Always a valid new code or empty.
 */
export function suggestProductCode(
  style: Pick<CatalogStyle, 'suggestedCode' | 'styleCode'>,
  supplierName: string,
): string {
  const suggested = alnumUpper(style.suggestedCode ?? '');
  if (NEW_PRODUCT_CODE_RE.test(suggested)) return suggested;
  let code = alnumUpper(style.styleCode);
  if (!/^[A-Z]/.test(code)) {
    const prefix = alnumUpper(supplierName).replace(/^[^A-Z]+/, '').slice(0, 3);
    code = `${prefix || 'C'}${code}`;
  }
  code = code.slice(0, 10);
  return NEW_PRODUCT_CODE_RE.test(code) ? code : '';
}

export const productCodeValid = (code: string): boolean => NEW_PRODUCT_CODE_RE.test(code.trim().toUpperCase());
export const productNameValid = (name: string): boolean => name.trim().length > 0 && name.trim().length <= BASE_GARMENT_NAME_MAX;

/** The SKU the first picked cell makes (`TEE-M-BLA`), for the summary line. */
export function exampleSku(
  productCode: string,
  cells: readonly PickedCell[],
  sizes: ResolvedAxis | null,
  colours: ResolvedAxis,
): string | null {
  const first = cells[0];
  if (!first) return null;
  const colour = colours.codeOf.get(first.colour.key);
  const size = first.size ? sizes?.codeOf.get(first.size.key) : null;
  if (!colour || (first.size && !size)) return null;
  return composeBlankSku(productCode.trim().toUpperCase() || 'CODE', size ?? null, colour);
}

// ── The call ────────────────────────────────────────────────────────────────

export interface AddInputArgs {
  locationId: string;
  style: Pick<CatalogStyle, 'id'>;
  supplierId: string;
  productCode: string;
  name: string;
  audience: BaseGarmentAudience;
  clientOrganizationId: string | null;
  axes: StyleAxes;
  cells: readonly PickedCell[];
  sizes: ResolvedAxis | null;
  colours: ResolvedAxis;
  defaultMode: AddStockMode;
  /** Only for a caller who may set costs; null = leave the cost alone. */
  cost: { amount: number; currency: CostCurrency | null } | null;
}

/**
 * The `addToLocation` input, or null while a cell has no code yet. Cells
 * whose Supply is the style's own are sent without one (the default covers
 * them), so the call stays small.
 */
export function buildAddInput(args: AddInputArgs): CatalogAddToLocationInput | null {
  if (args.cells.length === 0 || args.cells.length > CATALOG_MAX_CELLS) return null;
  const cells: CatalogAddToLocationInput['cells'] = [];
  const usedSizes = new Set<string>();
  const usedColours = new Set<string>();
  for (const cell of args.cells) {
    const colour = args.colours.codeOf.get(cell.colour.key);
    const size = cell.size ? args.sizes?.codeOf.get(cell.size.key) : null;
    if (!colour || (cell.size && !size)) return null;
    usedColours.add(colour);
    if (size) usedSizes.add(size);
    cells.push({
      catalogVariantId: cell.variant.id,
      size: size ?? null,
      colour,
      ...(cell.mode !== args.defaultMode ? { stockMode: cell.mode } : {}),
    });
  }
  // Only the codes a cell uses (a code picked for a size with no catalog variant here is left out).
  const axisInput = (axis: ResolvedAxis, used: ReadonlySet<string>) => ({
    none: false as const,
    codes: axis.input.codes.filter((code) => used.has(code)),
    newCodes: axis.input.newCodes.filter((entry) => used.has(entry.code)),
  });
  return {
    locationId: args.locationId,
    catalogStyleId: args.style.id,
    supplierId: args.supplierId,
    code: args.productCode.trim().toUpperCase(),
    name: args.name.trim(),
    audience: args.audience,
    ...(args.audience === 'client' && args.clientOrganizationId ? { clientOrganizationId: args.clientOrganizationId } : {}),
    sizes: args.axes.sizeless || !args.sizes ? { none: true, codes: [], newCodes: [] } : axisInput(args.sizes, usedSizes),
    colours: axisInput(args.colours, usedColours),
    cells,
    defaultMode: args.defaultMode,
    ...(args.cost ? { cost: { amount: args.cost.amount, ...(args.cost.currency ? { currency: args.cost.currency } : {}) } } : {}),
  };
}

/** A cost typed as text: blank = none; otherwise a number 0 to the cap with at most 2 decimals, or invalid. */
export function parseCost(text: string, max: number): { ok: true; amount: number | null } | { ok: false } {
  const trimmed = text.trim().replace(/,/g, '');
  if (trimmed === '') return { ok: true, amount: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { ok: false };
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount >= 0 && amount <= max ? { ok: true, amount } : { ok: false };
}
